import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
   BookingFailureLogParams,
   getBookingFailureLogs,
   getBookingFailureLog,
   updateBookingFailureStatus,
   createBookingFailureLogNote,
} from 'src/api/bookingFailureLogsApi';
import { queryFilter } from 'src/utils/proptypes';
import { MutationParams } from './useProgram';

export const BookingFailureLogQueryKeys = {
   GetBookingFailureLogs: 'getBookingFailureLogs',
   GetBookingFailureLog: 'getBookingFailureLog',
   UpdateStatusBookingFailureLog: 'updateStatusBookingFailureLog',
   CreateBookingFailureLogNote: 'createBookingFailureLogNote',
};

export const useGetBookingFailureLogs = (query: queryFilter & BookingFailureLogParams) => {
   return useQuery({
      queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLogs, query],
      queryFn: () => getBookingFailureLogs(query),
      staleTime: Infinity,
      retry: 0,
      placeholderData: prev => prev,
   });
};

export const useGetBookingFailureLog = (logId: string) => {
   return useQuery({
      queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLog, logId],
      queryFn: () => getBookingFailureLog(logId),
      staleTime: Infinity,
      retry: 0,
      placeholderData: prev => prev,
      enabled: !!logId,
   });
};

export const useUpdateBookingFailureStatus = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [BookingFailureLogQueryKeys.UpdateStatusBookingFailureLog],
      mutationFn: updateBookingFailureStatus,
      onError: error => {
         onErrorCallBack && onErrorCallBack(error);
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLogs] });
         await queryClient.invalidateQueries({ queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLog] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};

export const useCreateBookingFailureLogNote = (callBack: MutationParams) => {
   const { onSuccessCallBack, onErrorCallBack, onMutateCallBack, onSettledCallBack } = callBack;
   const queryClient = useQueryClient();

   return useMutation({
      mutationKey: [BookingFailureLogQueryKeys.CreateBookingFailureLogNote],
      mutationFn: createBookingFailureLogNote,
      onError: error => {
         onErrorCallBack && onErrorCallBack(error);
      },
      onSuccess: async () => {
         await queryClient.invalidateQueries({ queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLogs] });
         await queryClient.invalidateQueries({ queryKey: [BookingFailureLogQueryKeys.GetBookingFailureLog] });

         onSuccessCallBack && onSuccessCallBack();
      },
      onMutate: () => onMutateCallBack && onMutateCallBack(),
      onSettled: () => onSettledCallBack && onSettledCallBack(),
   });
};
